import type { TextInputProps } from '@meterup/atto';
import {
  CheckboxInput,
  colors,
  CompositeField,
  darkThemeSelector,
  devices,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  FieldContainer,
  FloatingField,
  FloatingImmediateErrorField,
  Icon,
  SearchInput,
  Segments,
  Select,
  SelectItem,
  styled,
  TextInput,
} from '@meterup/atto';
import { groupBy, isNumber } from 'lodash-es';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

import type { FilterOption, FilterValue, NegatedFilterValue } from '../../createColumnBuilder';
import {
  DateTimeRangePickerDropdownContents,
  useDateTimeRangePicker,
} from '../../../DateTimeRangePicker/DateTimeRangePicker';
import { TimePeriod } from '../../../DateTimeRangePicker/utils';
import { multiSelectFilterValue, parseMultiSelectFilterValue } from '../utils';

export const TableFiltersWiderContainer = styled('div', {
  width: '$320',
});
TableFiltersWiderContainer.displayName = 'TableFiltersWiderContainer';

// Sticks above the scrolling options, and reaches through the scrollable menu group's padding so
// its rows' strokes run edge to edge. The rows add that padding back inside.
const TableFiltersFieldControls = styled('div', {
  position: 'sticky',
  zIndex: 1,
  background: colors.bgApplicationLight,

  [devices.mouse]: {
    top: '-$6',
    margin: '-$6 -$6 0',
    paddingTop: '$6',
  },

  [devices.touch]: {
    top: '-$8',
    margin: '-$8 -$8 0',
    paddingTop: '$8',
  },

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
  },
});
TableFiltersFieldControls.displayName = 'TableFiltersFieldControls';

const tableFiltersFieldControlsRow = {
  strokeBottom: colors.strokeNeutralLight,

  [devices.mouse]: {
    paddingX: '$14',
  },

  [devices.touch]: {
    paddingX: '$16',
  },

  [darkThemeSelector]: {
    strokeBottom: colors.strokeNeutralDark,
  },
};

const TableFiltersFieldHeader = styled('div', tableFiltersFieldControlsRow, {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  alignItems: 'center',
  gap: '$8',
  paddingY: '$4',
});
TableFiltersFieldHeader.displayName = 'TableFiltersFieldHeader';

const TableFiltersFieldModes = styled('div', tableFiltersFieldControlsRow, {
  paddingY: '$8',
});
TableFiltersFieldModes.displayName = 'TableFiltersFieldModes';

// Context for managing nested dropdown state
const NestedDropdownContext = createContext<{
  isNestedDropdownActive: boolean;
  setNestedDropdownActive: (active: boolean) => void;
}>({
  isNestedDropdownActive: false,
  setNestedDropdownActive: () => {},
});

export const useTableFiltersFieldNestedDropdown = () => useContext(NestedDropdownContext);

export function TableFiltersFieldNestedDropdownProvider({ children }: { children: ReactNode }) {
  const [isNestedDropdownActive, setNestedDropdownActive] = useState(false);

  const contextValue = useMemo(
    () => ({ isNestedDropdownActive, setNestedDropdownActive }),
    [isNestedDropdownActive, setNestedDropdownActive],
  );

  return (
    <NestedDropdownContext.Provider value={contextValue}>{children}</NestedDropdownContext.Provider>
  );
}

type CommonFilterProps = {
  id: string;
  label: string;
};

type TextFilterProps = CommonFilterProps & {
  value: string;
  onChange: (id: string, value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
};

type SelectFilterProps = CommonFilterProps & {
  value: string;
  options: FilterOption[];
  handleFiltersChange: (id: string, key: string) => void;
  onKeyDown: React.KeyboardEventHandler<HTMLElement>;
  onValueSelected?: () => void;
};

type MultiSelectFilterProps = CommonFilterProps & {
  value: FilterValue;
  options: FilterOption[];
  negatable?: boolean;
  handleFiltersChange: (id: string, value: string[] | NegatedFilterValue) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  onValueSelected?: () => void;
};

const TableFiltersFieldRow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  padding: '$6 $8',
  width: '100%',
});
TableFiltersFieldRow.displayName = 'TableFiltersFieldRow';

export function TableFiltersFieldSelectFilter({
  id,
  label,
  value,
  handleFiltersChange,
  options,
  onKeyDown,
  onValueSelected,
}: SelectFilterProps) {
  if (options) {
    return (
      <FieldContainer background="application">
        <CompositeField
          label={label}
          element={
            <TableFiltersFieldRow>
              <Select
                aria-label={label}
                value={value}
                onValueChange={(key: string) => {
                  handleFiltersChange(id, key);
                  onValueSelected?.();
                }}
                size="medium"
                width="100%"
                onKeyDown={onKeyDown}
              >
                {options.map((option) => (
                  <SelectItem
                    key={option.value}
                    textValue={typeof option.label === 'string' ? option.label : option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </TableFiltersFieldRow>
          }
        />
      </FieldContainer>
    );
  }
}

export function TableFiltersFieldDebouncedInput({
  id,
  value,
  label,
  onChange,
  onKeyDown,
  parseValue,
  onValueSelected,
  disableAutoFocus,
  ...restProps
}: Omit<TextInputProps, 'onChange'> & {
  id: string;
  onChange: (id: string, value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  parseValue?: (value: string) => string;
  onValueSelected?: () => void;
  disableAutoFocus?: boolean;
}) {
  const [localVal, setLocalVal] = useState(value ?? '');
  const inputRef = useRef<HTMLLabelElement>(null);
  const debouncedCallback = useDebouncedCallback((val) => {
    onChange(id, val);
  }, 250);

  const handleOnChange = useCallback(
    (val: string) => {
      const parsedValue = parseValue ? parseValue(val) : val;
      setLocalVal(parsedValue);
      debouncedCallback(parsedValue);
    },
    [setLocalVal, debouncedCallback, parseValue],
  );

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter') {
        onValueSelected?.();
      }
      onKeyDown?.(e);
    },
    [onValueSelected, onKeyDown],
  );

  // Focus the input when the component mounts (when dropdown opens)
  useEffect(() => {
    if (disableAutoFocus) return undefined;

    const timeoutId = setTimeout(() => {
      const inputElement = inputRef.current?.querySelector('input');
      if (inputElement) {
        inputElement.focus();
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [disableAutoFocus]);

  return (
    <FloatingField
      label={label}
      element={
        <TextInput
          {...restProps}
          ref={inputRef}
          id={id}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          value={localVal}
          size="small"
          width="100%"
        />
      }
    />
  );
}

export function TableFiltersFieldText({
  id,
  value,
  label,
  onChange,
  onKeyDown,
  onValueSelected,
}: TextFilterProps & { onValueSelected?: () => void }) {
  return (
    <CompositeField
      label={label}
      element={
        <TableFiltersFieldDebouncedInput
          id={id}
          label={label}
          onChange={onChange}
          onKeyDown={onKeyDown}
          value={value}
          onValueSelected={onValueSelected}
        />
      }
    />
  );
}

const NumberRangeFieldsIcon = styled(Icon, {
  width: '$12',
  height: '$12',
});
NumberRangeFieldsIcon.displayName = 'NumberRangeFieldsIcon';

export function TableFiltersFieldNumber({
  id,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
  onValueSelected,
}: {
  id: string;
  value: number | null;
  label: string;
  handleFiltersChange: (id: string, value: number | null) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  onValueSelected?: () => void;
}) {
  return (
    <CompositeField
      label={label}
      element={
        <TableFiltersFieldDebouncedInput
          id={id}
          label={label}
          onChange={(inputId, key) => {
            const num = Number(key);
            const val = key === '' ? null : num;
            handleFiltersChange(inputId, val);
          }}
          onKeyDown={onKeyDown}
          value={value === null ? '' : String(value)}
          type="number"
          onValueSelected={onValueSelected}
        />
      }
    />
  );
}

export function TableFiltersFieldNumberRange({
  id: idProp,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
  onValueSelected,
}: {
  id: string;
  value: [number | null, number | null];
  label: string;
  handleFiltersChange: (id: string, value: [number | null, number | null]) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  onValueSelected?: () => void;
}) {
  const isInvalidRange = isNumber(value[0]) && isNumber(value[1]) && value[0] > value[1];
  const rangeRef = useRef<HTMLDivElement>(null);

  // Auto-focus the first input (min) when the dropdown opens
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const firstInput = rangeRef.current?.querySelector('input');
      if (firstInput) {
        firstInput.focus();
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div ref={rangeRef}>
      <FloatingImmediateErrorField
        errorMessage={isInvalidRange ? 'Minimum cannot be above the maximum.' : undefined}
        label="Number range"
        element={
          <TableFiltersFieldRow>
            <CompositeField
              label="Min"
              element={
                <TableFiltersFieldDebouncedInput
                  id={`${idProp}-min`}
                  label={`${label} min.`}
                  prefix="Min"
                  onChange={(id, key) => {
                    const min = Number(key);
                    const max = value[1];
                    const val = key === '' ? null : min;
                    handleFiltersChange(idProp, [val, max]);
                  }}
                  onKeyDown={onKeyDown}
                  value={String(value[0])}
                  type="number"
                  onValueSelected={onValueSelected}
                />
              }
            />
            <NumberRangeFieldsIcon icon="arrow-right" />
            <CompositeField
              label="Max"
              element={
                <TableFiltersFieldDebouncedInput
                  id={`${idProp}-max`}
                  label={`${label} max.`}
                  prefix="Max"
                  onChange={(id, key) => {
                    const min = value[0];
                    const max = Number(key);
                    const val = key === '' ? null : max;
                    handleFiltersChange(idProp, [min, val]);
                  }}
                  onKeyDown={onKeyDown}
                  value={String(value[1])}
                  type="number"
                  onValueSelected={onValueSelected}
                  disableAutoFocus
                />
              }
            />
          </TableFiltersFieldRow>
        }
      />
    </div>
  );
}

export function TableFiltersFieldSelect({
  id,
  handleFiltersChange,
  options,
  onKeyDown,
  onValueSelected,
  value,
}: Omit<SelectFilterProps, 'label'>) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  // Auto-focus the first option when the dropdown opens
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchRef.current?.focus();
    }, 50);
    return () => clearTimeout(timeoutId);
  }, []);

  const filteredOptions = useMemo(
    () =>
      options?.filter(
        (o) =>
          value === o.value ||
          (o.textValue ?? (typeof o.label === 'string' ? o.label : o.value))
            .toLowerCase()
            .includes(query.toLowerCase()),
      ),
    [options, query, value],
  );

  if (!options?.length) {
    return (
      <div>
        <DropdownMenuItem disabled>No options found</DropdownMenuItem>
      </div>
    );
  }

  return (
    <div>
      <SearchInput
        inputRef={searchRef}
        value={query}
        onChange={setQuery}
        onKeyDown={onKeyDown}
        size="small"
        width="100%"
        preset="none"
        aria-label="Filter options"
      />
      {filteredOptions?.length ? (
        Object.entries(groupBy(filteredOptions, (option) => option.group ?? '')).map(
          ([group, groupOptions]) => (
            <DropdownMenuRadioGroup
              key={group}
              label={group || undefined}
              isNested
              value={value}
              onValueChange={(newValue: string) => {
                handleFiltersChange(id, newValue);
                onValueSelected?.();
              }}
            >
              {groupOptions.map((option) => (
                <DropdownMenuRadioItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  onKeyDown={onKeyDown}
                  showRadio
                >
                  {option.label ?? option.value}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          ),
        )
      ) : (
        <DropdownMenuItem disabled>No options found</DropdownMenuItem>
      )}
    </div>
  );
}

export function TableFiltersFieldMultiSelect({
  id,
  value,
  handleFiltersChange,
  options,
  onKeyDown,
  negatable = false,
}: Omit<MultiSelectFilterProps, 'label'>) {
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [query, setQuery] = useState('');

  const { values, negated } = useMemo(() => parseMultiSelectFilterValue(value), [value]);
  const setValues = useCallback(
    (next: string[], nextNegated = negated) =>
      handleFiltersChange(id, multiSelectFilterValue(next, nextNegated)),
    [handleFiltersChange, id, negated],
  );

  // Auto-focus the first option when the dropdown opens
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchRef.current?.focus();
    }, 50);
    return () => clearTimeout(timeoutId);
  }, []);

  const queriedOptions = useMemo(
    () =>
      options?.filter((o) =>
        (o.textValue ?? (typeof o.label === 'string' ? o.label : o.value))
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [options, query],
  );

  // Captured on open so options don't jump around while they're being toggled.
  const [selectedOnOpen] = useState(() => new Set(values));

  const filteredOptions = useMemo(() => {
    const matching = options?.filter(
      (o) =>
        values.includes(o.value) ||
        (o.textValue ?? (typeof o.label === 'string' ? o.label : o.value))
          .toLowerCase()
          .includes(query.toLowerCase()),
    );
    return (
      matching && [
        ...matching.filter((o) => selectedOnOpen.has(o.value)),
        ...matching.filter((o) => !selectedOnOpen.has(o.value)),
      ]
    );
  }, [options, query, values, selectedOnOpen]);

  const selectableOptions = useMemo(
    () => queriedOptions?.filter((o) => !o.disabled) ?? [],
    [queriedOptions],
  );

  const selectedVisibleCount = selectableOptions.filter((o) => values.includes(o.value)).length;
  const allSelected =
    selectableOptions.length > 0 && selectedVisibleCount === selectableOptions.length;
  const someSelected = selectedVisibleCount > 0 && !allSelected;
  const anySelected = selectedVisibleCount > 0;

  const handleToggleAll = useCallback(() => {
    if (anySelected) {
      const visibleIds = new Set(selectableOptions.map((o) => o.value));
      setValues(values.filter((v) => !visibleIds.has(v)));
      return;
    }
    const merged = new Set(values);
    for (const o of selectableOptions) {
      merged.add(o.value);
    }
    setValues(Array.from(merged));
  }, [anySelected, selectableOptions, setValues, values]);

  if (options) {
    return (
      <div>
        <TableFiltersFieldControls>
          {negatable && (
            <TableFiltersFieldModes>
              <Segments
                size="small"
                segments={[
                  { label: 'Is', selected: !negated, onClick: () => setValues(values, false) },
                  { label: 'Is not', selected: negated, onClick: () => setValues(values, true) },
                ]}
              />
            </TableFiltersFieldModes>
          )}
          <TableFiltersFieldHeader>
            {selectableOptions.length > 0 && (
              <CheckboxInput
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleToggleAll}
                aria-label={allSelected ? 'Deselect all' : 'Select all'}
              />
            )}
            <SearchInput
              inputRef={searchRef}
              value={query}
              onChange={setQuery}
              onKeyDown={onKeyDown}
              size="small"
              width="100%"
              preset="none"
              aria-label="Filter options"
            />
          </TableFiltersFieldHeader>
        </TableFiltersFieldControls>
        {filteredOptions?.length ? (
          filteredOptions.map((option) => {
            const isSelected = values.includes(option.value);
            return (
              <DropdownMenuCheckboxItem
                key={option.value}
                closeOnClick={false}
                onCheckedChange={(checked) => {
                  setValues(
                    checked ? [...values, option.value] : values.filter((v) => v !== option.value),
                  );
                }}
                onKeyDown={(e) => onKeyDown?.(e as any)}
                checked={isSelected}
                disabled={option.disabled}
              >
                {option.label ?? option.value}
              </DropdownMenuCheckboxItem>
            );
          })
        ) : (
          <DropdownMenuItem disabled>No options found</DropdownMenuItem>
        )}
      </div>
    );
  }
  return null;
}

export function TableFiltersFieldDateRange({
  id,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
  onValueSelected,
  showPresets = true,
}: {
  id: string;
  value: [string | null, string | null];
  label: string;
  handleFiltersChange: (id: string, value: [string | null, string | null]) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  onValueSelected?: () => void;
  /** Offer the relative time-period presets alongside the custom range inputs. */
  showPresets?: boolean;
}) {
  const hasValue = !!value[0] && !!value[1];

  const state = useDateTimeRangePicker({
    fixAnchorToNow: false,
    allowCustom: true,
    defaultValue: hasValue || !showPresets ? TimePeriod.Custom : TimePeriod.OneDay,
    defaultStart: value[0] ?? undefined,
    defaultEnd: value[1] ?? undefined,
  });

  const handleChange = useEffectEvent(handleFiltersChange);

  useEffect(() => {
    handleChange(id, [state.startDate, state.endDate]);
  }, [id, state.startDate, state.endDate]);

  return (
    <FloatingImmediateErrorField
      label={label}
      element={
        <DateTimeRangePickerDropdownContents
          state={state}
          onValueChange={onValueSelected}
          onKeyDown={onKeyDown}
          hidePresets={!showPresets}
          options={[
            TimePeriod.ThirtyMinutes,
            TimePeriod.OneHour,
            TimePeriod.TwoHours,
            TimePeriod.ThreeHours,
            TimePeriod.SixHours,
            TimePeriod.TwelveHours,
            TimePeriod.OneDay,
            TimePeriod.SevenDays,
            TimePeriod.ThirtyDays,
          ]}
        />
      }
    />
  );
}
